import React, { useState, useEffect } from 'react';
import Navigation from '../components/Navigation';
import { supabase } from '../lib/supabase';
import { Plus, Search, Pencil, Trash2, X, Loader2, Phone, Mail, Building, User, ExternalLink } from 'lucide-react';
import AgencyForm from '../components/AgencyForm';
import { toast } from '../lib/toast';
import { Database } from '../types/supabase';

type Agency = Database['public']['Tables']['agencies']['Row'];

export default function Agencies({ navigate }: { navigate: (to: string) => void }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingAgency, setEditingAgency] = useState<Agency | null>(null);
  const [loading, setLoading] = useState(true);
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [filteredAgencies, setFilteredAgencies] = useState<Agency[]>([]);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  useEffect(() => {
    fetchAgencies();
  }, []);

  const handleEdit = (agency: Agency) => {
    setEditingAgency(agency);
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this customer?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('agencies')
        .delete()
        .eq('id', id);

      if (error) throw error;
      const updatedAgencies = agencies.filter(agency => agency.id !== id);
      setAgencies(updatedAgencies);
      setFilteredAgencies(updatedAgencies);
      toast.success('Customer deleted successfully');
    } catch (error) {
      console.error('Error deleting customer:', error);
      toast.error('Failed to delete customer');
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    filterAgencies();
  };

  const filterAgencies = () => {
    if (!searchQuery.trim()) {
      setFilteredAgencies(agencies);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = agencies.filter(agency => 
      agency.name.toLowerCase().includes(query) ||
      agency.email.toLowerCase().includes(query)
    );
    setFilteredAgencies(filtered);
  };

  const fetchAgencies = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;

      const { data, error } = await supabase
        .from('agencies')
        .select('*')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      setAgencies(data || []);
      setFilteredAgencies(data || []);
    } catch (error) {
      console.error('Error fetching agencies:', error);
      toast.error('Failed to load agencies');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <Navigation
        onSignOut={async () => await supabase.auth.signOut()}
        tasks={[]}
        currentView="all"
        onViewChange={() => {}}
        navigate={navigate}
      />

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 sm:px-0">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-0">Customers</h1>
            <div className="flex space-x-2">
              <button
                onClick={() => {
                  setEditingAgency(null);
                  setShowForm(true);
                }}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <Plus className="h-5 w-5 mr-2" />
                Add Customer
              </button>
              <div className="relative">
                <input
                  type="text"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Search customers..."
                  value={searchQuery}
                  onChange={handleSearch}
                />
              </div>
            </div>
          </div>

          {showForm && (
            <div className="bg-white dark:bg-gray-800 shadow sm:rounded-lg p-6 mb-8">
              <AgencyForm
                onSuccess={() => {
                  setShowForm(false);
                  fetchAgencies();
                }}
                onCancel={() => {
                  setShowForm(false);
                  setEditingAgency(null);
                }}
                initialData={editingAgency || undefined}
              />
            </div>
          )}

          {/* Agency List */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600 mx-auto"></div>
              </div>
            ) : agencies.length === 0 ? (
              <div className="text-center py-12 col-span-1 md:col-span-2 lg:col-span-3">
                <p className="text-gray-500 dark:text-gray-400">
                  No customers found. Create one to get started!
                </p>
              </div>
            ) : (
              filteredAgencies.map((agency) => (
                <div key={agency.id} className="bg-white dark:bg-gray-800 shadow-md hover:shadow-lg transition-shadow duration-300 rounded-xl overflow-hidden">
                  {/* Card Header with Logo/Initials */}
                  <div className="relative bg-gradient-to-r from-indigo-500 to-purple-600 h-24 p-4">
                    <div className="absolute top-4 right-4 flex space-x-2">
                      <button
                        onClick={() => handleEdit(agency)}
                        className="p-1.5 bg-white/20 hover:bg-white/30 rounded-full text-white transition-colors"
                        title="Edit customer"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(agency.id)}
                        className="p-1.5 bg-white/20 hover:bg-red-400/30 rounded-full text-white transition-colors"
                        title="Delete customer"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    
                    {/* Logo or Initials */}
                    <div className="absolute -bottom-10 left-6 w-20 h-20 rounded-xl bg-white dark:bg-gray-700 shadow-md flex items-center justify-center overflow-hidden">
                      {agency.logo_url ? (
                        <img 
                          src={agency.logo_url} 
                          alt={`${agency.name} logo`} 
                          className="w-full h-full object-contain p-2"
                          onError={(e) => {
                            // Fallback to initials if image fails to load
                            e.currentTarget.style.display = 'none';
                            const parent = e.currentTarget.parentElement;
                            if (parent) {
                              parent.innerHTML = `<div class="text-2xl font-bold text-indigo-600 dark:text-indigo-400">${agency.name.substring(0, 2).toUpperCase()}</div>`;
                            }
                          }}
                        />
                      ) : (
                        <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                          {agency.name.substring(0, 2).toUpperCase()}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Card Content */}
                  <div className="pt-12 px-6 pb-4">
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white truncate" title={agency.name}>
                      {agency.name}
                    </h3>
                    
                    <div className="mt-4 space-y-2">
                      <div className="flex items-start">
                        <User className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                        <span className="ml-2 text-sm text-gray-600 dark:text-gray-300">
                          {agency.contact_first_name} {agency.contact_last_name}
                        </span>
                      </div>
                      
                      <div className="flex items-start">
                        <Mail className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                        <span className="ml-2 text-sm text-gray-600 dark:text-gray-300 truncate" title={agency.email}>
                          {agency.email}
                        </span>
                      </div>
                      
                      <div className="flex items-start">
                        <Phone className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                        <span className="ml-2 text-sm text-gray-600 dark:text-gray-300">
                          {agency.phone}
                        </span>
                      </div>
                      
                      {agency.additional_info && (
                        <div className="flex items-start">
                          <Building className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                          <span className="ml-2 text-sm text-gray-600 dark:text-gray-300 line-clamp-2" title={agency.additional_info}>
                            {agency.additional_info}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Card Footer */}
                  <div className="px-6 py-3 border-t border-gray-100 dark:border-gray-700">
                    <button 
                      className="w-full flex items-center justify-center text-sm font-medium text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
                      onClick={() => handleEdit(agency)}
                    >
                      <ExternalLink className="h-4 w-4 mr-1.5" />
                      View Details
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </main>
    </div>
  );
}